// System
import { useSnackbar } from 'notistack';
import { BaseSyntheticEvent, useCallback, useEffect, useMemo } from 'react';
// Redux
import { useSelector, useDispatch } from 'react-redux';
import {
  setSelectedCampaign,
  setShowCampaignDrawer,
  setShowCancelCampaignDrawer,
} from '@Modal/reducers/campaigns/drawer/campaignDrawerSlice';
// Form
import { useFormState } from 'react-hook-form';
import {
  useManageSetupCampaignSchema,
  useSetupCampaignForm,
} from '@Forms/campaign/SetupCampaignFormContext';
// Types
import { AppDispatch } from 'src/app/store';
import { StoreInterface } from 'src/app/types';
import {
  useUpdateFreeBetCampaignMutation,
  PaginatedFreeBetsCampaignsDocument,
} from '@Src/generated.graphql';
import { useSetupCampaignUseCaseMapper } from '@Hooks/campaigns/SetupCampaign/useSetupCampaignUseCaseMapper';
import { useSetupCampaignUseCaseSchema } from '@Hooks/campaigns/SetupCampaign/useSetupCampaignUseCaseSchema';
import { CampaignItemDto } from '@contracts/api/dto/campaign/CampaignDto';
import { useCampaignsTable } from './useCampaignsTable';

export const useCampaignDrawer = () => {
  // Redux
  const dispatch = useDispatch<AppDispatch>();
  const [updateCampaign] = useUpdateFreeBetCampaignMutation();
  const { limit, page, campaignTableFilters } = useCampaignsTable();
  const { showCampaignDrawer, showCancelCampaignDrawer, selectedCampaignData } = useSelector(
    (state: StoreInterface) => state?.campaignDrawer
  );

  const { setSchema } = useManageSetupCampaignSchema();
  const mapper = useSetupCampaignUseCaseMapper(selectedCampaignData);
  const schema = useSetupCampaignUseCaseSchema();

  // Other Hooks
  const { enqueueSnackbar } = useSnackbar();

  // Handle Form Cancels
  const handleCreateCancel = () => {
    if (isDirty) {
      return dispatch(setShowCancelCampaignDrawer(true));
    } else {
      return dispatch(setShowCampaignDrawer(false));
    }
  };

  const handleCancelTakeMeBack = (action: string) => {
    if (action === 'back') {
      dispatch(setShowCancelCampaignDrawer(false));
      dispatch(setShowCampaignDrawer(true));
    } else {
      reset();
      dispatch(setShowCancelCampaignDrawer(false));
      dispatch(setShowCampaignDrawer(false));
    }
  };

  const handleCancelChanges = () => {
    dispatch(setShowCancelCampaignDrawer(false));
    dispatch(setShowCampaignDrawer(false));
    reset();
  };

  const setCampaignDrawerMapperHandler = useCallback(async () => {
    setSchema(schema);
    const formState = await mapper.toFormState(selectedCampaignData._id);
    reset(formState);
  }, [selectedCampaignData]);

  useEffect(() => {
    if (selectedCampaignData) {
      setCampaignDrawerMapperHandler();
    }
  }, [selectedCampaignData, setCampaignDrawerMapperHandler]);

  // Form Controls
  const { control, handleSubmit, reset } = useSetupCampaignForm();
  const { isDirty, errors } = useFormState({ control });

  const handleCampaignRowClick = async (row: CampaignItemDto) => {
    dispatch(setSelectedCampaign(row));
    dispatch(setShowCampaignDrawer(true));
  };

  const handleSubmitGeneralForm = useMemo(
    () => (e?: BaseSyntheticEvent) => {
      return handleSubmit(async (formData) => {
        try {
          await updateCampaign({
            variables: {
              input: {
                providerId: selectedCampaignData?.providerId ?? '',
                campaignId: selectedCampaignData?._id,
                endDate: formData.endDate,
              },
            },
            refetchQueries: [
              {
                query: PaginatedFreeBetsCampaignsDocument,
                variables: { limit, page, filter: campaignTableFilters, search: '' },
              },
            ],
          });
          enqueueSnackbar('Campaign was successfully updated', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });

          dispatch(setShowCampaignDrawer(false));
        } catch (error) {
          enqueueSnackbar(error.message, {
            variant: 'error',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        }
      })(e);
    },
    []
  );

  return {
    // Store
    selectedCampaignData,
    showCampaignDrawer,
    showCancelCampaignDrawer,
    submittable: isDirty && Object.values(errors).length == 0,
    // Actions
    setSelectedCampaign,
    setShowCampaignDrawer,
    setShowCancelCampaignDrawer,
    // Handlers
    handleCreateCancel,
    handleCancelChanges,
    handleCampaignRowClick,
    handleCancelTakeMeBack,
    handleSubmitGeneralForm,
  };
};
